import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { detectTenantSlug, useTenant } from '@/contexts/TenantContext';
import { ChevronLeft, ChevronRight, Check, Minus, Plus, Loader2, Globe, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { UPLOADS_BASE } from '@/api';
import { AIBookingAssistant } from '@/components/AIBookingAssistant';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                    'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const MONTHS_EN = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];
const DAYS_IT = ['Lu','Ma','Me','Gi','Ve','Sa','Do'];
const DAYS_EN = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const DAY_NAMES_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}
function fmt(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseFmt(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function formatDayLabel(dateStr) {
  const d = parseFmt(dateStr);
  const name = DAY_NAMES_IT[d.getDay()];
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${name} ${dd}/${mm}`;
}

/** Compact day checkboxes grid */
function DayCheckboxGrid({ stayDays, selectedDays, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {stayDays.map(day => {
        const checked = selectedDays.includes(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() => {
              const next = checked
                ? selectedDays.filter(d => d !== day)
                : [...selectedDays, day];
              onChange(next);
            }}
            className={`px-2 py-1 text-xs rounded border transition-colors ${
              checked
                ? 'border-[#c9a84c] bg-[#c9a84c]/15 text-[#c9a84c]'
                : 'border-[var(--lux-border-3)] text-[var(--lux-text-muted-3)] hover:border-[var(--lux-text-muted-3)]'
            }`}
          >
            {formatDayLabel(day)}
          </button>
        );
      })}
    </div>
  );
}

/** Per-day service day selector (all vs specific) */
function PerDaySelector({ stayDays, sel, onUpdate, nightsCount }) {
  const allDays = sel.applies_to_all_days !== false;
  return (
    <div className="mt-2 ml-1 pl-3 border-l border-[#222] space-y-1.5">
      <p className="text-[var(--lux-text-muted-3)] text-xs mb-1">Quando?</p>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="radio" checked={allDays} onChange={() => onUpdate({ applies_to_all_days: true, selected_days: [] })}
          className="accent-[#c9a84c]" />
        <span className="text-xs text-[var(--lux-text-sec)]">Tutto il soggiorno ({nightsCount} gg)</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="radio" checked={!allDays} onChange={() => onUpdate({ applies_to_all_days: false, selected_days: [] })}
          className="accent-[#c9a84c]" />
        <span className="text-xs text-[var(--lux-text-sec)]">Giorni specifici</span>
      </label>
      {!allDays && (
        <DayCheckboxGrid
          stayDays={stayDays}
          selectedDays={sel.selected_days || []}
          onChange={days => onUpdate({ selected_days: days })}
        />
      )}
    </div>
  );
}

/** Rental/NCC variant selector + custom fields */
function RentalSelector({ svc, sel, stayDays, numGuests, onUpdate, lang }) {
  const variants = svc.variants || [];
  const selectedId = sel.selected_variant_id || '';
  const selectedVariant = variants.find(v => v.id === selectedId);

  return (
    <div className="mt-3 space-y-3 ml-1 pl-3 border-l border-[#222]">
      <p className="text-[var(--lux-text-muted-3)] text-xs uppercase tracking-widest">Seleziona variante:</p>
      <div className="space-y-2">
        {variants.map(variant => (
          <label key={variant.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-[var(--lux-surface-3)] hover:border-[#c9a84c]/30 transition-colors">
            <input
              type="radio"
              name={`variant-${svc.id}`}
              value={variant.id}
              checked={selectedId === variant.id}
              onChange={() => onUpdate({
                selected_variant_id: variant.id,
                selected_variant_name: variant.name_it,
                custom_field_values: {},
              })}
              className="accent-[#c9a84c]"
            />
            <span className="text-sm text-white">
              {lang === 'it' ? variant.name_it : (variant.name_en || variant.name_it)}
            </span>
          </label>
        ))}
      </div>

      {/* Custom fields for selected variant */}
      {selectedVariant && (selectedVariant.custom_fields || []).length > 0 && (
        <div className="space-y-3 pt-1">
          <p className="text-[var(--lux-text-muted-3)] text-xs">Informazioni necessarie:</p>
          {selectedVariant.custom_fields.map(field => (
            <div key={field.id}>
              <label className="label-muted block mb-1 text-xs">
                {lang === 'it' ? field.label_it : (field.label_en || field.label_it)}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </label>
              {field.field_type === 'textarea' ? (
                <textarea
                  className="input-dark text-sm"
                  rows={3}
                  placeholder={field.placeholder_it || ''}
                  value={(sel.custom_field_values || {})[field.label_it] || ''}
                  onChange={e => onUpdate({
                    custom_field_values: { ...(sel.custom_field_values || {}), [field.label_it]: e.target.value }
                  })}
                />
              ) : (
                <input
                  type="text"
                  className="input-dark text-sm"
                  placeholder={field.placeholder_it || ''}
                  value={(sel.custom_field_values || {})[field.label_it] || ''}
                  onChange={e => onUpdate({
                    custom_field_values: { ...(sel.custom_field_values || {}), [field.label_it]: e.target.value }
                  })}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function MealVariantRow({ label, mealKey, daysKey, allKey, price, numPersons, sel, stayDays, onUpdate }) {
  const checked = sel[mealKey] || false;
  const allDays = sel[allKey] !== false;
  const selectedDays = sel[daysKey] || [];
  const dayCount = allDays ? stayDays.length : selectedDays.length;
  const subtotal = price * numPersons * dayCount;

  return (
    <div className="p-3 bg-[#070707] rounded-lg border border-[var(--lux-surface-3)]">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer flex-1">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => onUpdate({ [mealKey]: e.target.checked, [allKey]: true, [daysKey]: [] })}
            className="accent-[#c9a84c]"
          />
          <span className="text-sm text-white">{label}</span>
        </label>
        {price > 0 ? (
          <span className="text-xs text-[#c9a84c]">
            €{price}/pp · {checked ? `€${subtotal.toFixed(0)}` : '—'}
          </span>
        ) : (
          <span className="text-xs text-amber-400">Su richiesta</span>
        )}
      </div>
      {checked && (
        <div className="mt-2 ml-1 pl-3 border-l border-[#222] space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={allDays} onChange={() => onUpdate({ [allKey]: true, [daysKey]: [] })}
              className="accent-[#c9a84c]" />
            <span className="text-xs text-[var(--lux-text-sec)]">Tutti i giorni ({stayDays.length} gg)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={!allDays} onChange={() => onUpdate({ [allKey]: false, [daysKey]: [] })}
              className="accent-[#c9a84c]" />
            <span className="text-xs text-[var(--lux-text-sec)]">Giorni specifici</span>
          </label>
          {!allDays && (
            <DayCheckboxGrid
              stayDays={stayDays}
              selectedDays={selectedDays}
              onChange={days => onUpdate({ [daysKey]: days })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CalendarMonth({ year, month, calData, checkin, checkout, hoveredDate, onDateHover, onDateClick, lang, minStay }) {
  const months = lang === 'it' ? MONTHS_IT : MONTHS_EN;
  const days = lang === 'it' ? DAYS_IT : DAYS_EN;
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startDow = firstDay.getDay() - 1; // Mon=0
  if (startDow < 0) startDow = 6;

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);

  const today = fmt(new Date());

  return (
    <div>
      <div className="grid grid-cols-7 mb-2">
        {days.map((d) => (
          <div key={d} className="text-center text-[var(--lux-text-muted-3)] text-xs py-2 uppercase tracking-widest">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const info = calData[dateStr];
          const available = info?.available;
          const price = info?.price;
          const isWeekend = info?.is_weekend;
          const isCheckin = checkin === dateStr;
          const isCheckout = checkout === dateStr;
          const isBetween = checkin && checkout && dateStr > checkin && dateStr < checkout;
          const isHovered = checkin && !checkout && hoveredDate && dateStr > checkin && dateStr <= hoveredDate;
          const isPast = dateStr < today;

          let classes = 'relative flex flex-col items-center justify-center py-2 rounded text-xs cursor-pointer transition-all select-none ';
          if (isPast || !available) {
            classes += 'text-[var(--lux-border-3)] cursor-not-allowed line-through ';
          } else if (isCheckin || isCheckout) {
            classes += 'bg-[#c9a84c] text-black font-semibold ';
          } else if (isBetween || isHovered) {
            classes += 'bg-[#c9a84c]/15 text-white ';
          } else {
            classes += `text-white hover:bg-[var(--lux-border-1)] ${isWeekend ? 'text-[#c9a84c]' : ''}`;
          }

          return (
            <div
              key={dateStr}
              className={classes}
              onClick={() => !isPast && available && onDateClick(dateStr)}
              onMouseEnter={() => onDateHover(dateStr)}
              data-testid={`calendar-day-${dateStr}`}
            >
              <span>{d}</span>
              {available && price > 0 && (
                <span className="text-[8px] text-[#c9a84c] leading-none mt-0.5 hidden sm:block">
                  {Math.round(price)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BookingPortal() {
  const { propertySlug } = useParams();
  const { lang, setLang, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const tenantSlug = detectTenantSlug();
  const { whitelabel: wl } = useTenant();
  const [property, setProperty] = useState(null);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calData, setCalData] = useState({});
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [hoveredDate, setHoveredDate] = useState('');
  const [services, setServices] = useState([]);
  const [extras, setExtras] = useState([]);
  const [selectedServices, setSelectedServices] = useState({});
  const [selectedExtras, setSelectedExtras] = useState({});
  const [guestForm, setGuestForm] = useState({ name: '', email: '', phone: '', num_guests: 2, special_requests: '' });
  const [acceptRules, setAcceptRules] = useState(false);
  const [step, setStep] = useState('calendar');
  const [submitting, setSubmitting] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [propertyId, setPropertyId] = useState('');
  const [activeSlug, setActiveSlug] = useState(propertySlug || '');
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);

  const slug = activeSlug || propertySlug || '';

  const months = lang === 'it' ? MONTHS_IT : MONTHS_EN;

  // Load all properties for multi-property selector
  useEffect(() => {
    axios.get(`${API}/booking-portal/properties`).then(r => {
      setAllProperties(r.data || []);
      // If no slug in URL, use the first available
      if (!propertySlug && r.data?.length > 0 && !activeSlug) {
        setActiveSlug(r.data[0].slug);
      }
    }).catch(() => {}).finally(() => setPropertiesLoaded(true));
  }, []);

  useEffect(() => {
    // Aspetta che la lista proprietà sia stata caricata prima di tentare il fetch,
    // altrimenti slug potrebbe ancora essere '' e causare una richiesta inutile.
    if (!propertiesLoaded || !slug) return;
    setLoading(true);
    setCalData({});
    setCheckin('');
    setCheckout('');
    setSelectedServices({});
    setSelectedExtras({});
    (async () => {
      try {
        const res = await axios.get(`${API}/booking-portal/property/${slug}`);
        setProperty(res.data);
        setPropertyId(res.data.id);
      } catch {
        toast.error('Proprietà non trovata');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, propertiesLoaded]);

  const loadCalendar = useCallback(async (pid, y, m) => {
    if (!pid) return;
    try {
      const res = await axios.get(`${API}/booking-portal/calendar?property_id=${pid}&year=${y}&month=${m + 1}`);
      setCalData(prev => ({ ...prev, ...res.data }));
    } catch {}
  }, []);

  useEffect(() => {
    if (propertyId) {
      loadCalendar(propertyId, calYear, calMonth);
    }
  }, [propertyId, calYear, calMonth, loadCalendar]);

  useEffect(() => {
    if (propertyId) {
      (async () => {
        try {
          const [svcRes, extRes] = await Promise.all([
            axios.get(`${API}/services/public?property_id=${propertyId}`),
            axios.get(`${API}/extras/public?property_id=${propertyId}`),
          ]);
          setServices(svcRes.data);
          setExtras(extRes.data);
        } catch {}
      })();
    }
  }, [propertyId]);

  const servicesRef = useRef(null);

  const handleDateClick = (dateStr) => {
    if (!checkin || (checkin && checkout)) {
      setCheckin(dateStr);
      setCheckout('');
      setHoveredDate('');
    } else if (dateStr > checkin) {
      const nights = (parseFmt(dateStr) - parseFmt(checkin)) / 86400000;
      const minStay = property?.min_stay || 1;
      if (nights < minStay) {
        toast.warning(`Soggiorno minimo: ${minStay} ${minStay === 1 ? 'notte' : 'notti'}`);
        return;
      }
      setCheckout(dateStr);
      // Scroll down to show services/info after dates are selected
      setTimeout(() => {
        if (servicesRef.current) {
          servicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    } else {
      setCheckin(dateStr);
      setCheckout('');
    }
  };

  const nightsCount = checkin && checkout
    ? Math.round((parseFmt(checkout) - parseFmt(checkin)) / 86400000)
    : 0;

  // All nights of stay as date strings
  const stayDays = useMemo(() => {
    if (!checkin || !checkout) return [];
    const days = [];
    let d = parseFmt(checkin);
    const end = parseFmt(checkout);
    while (d < end) {
      days.push(fmt(d));
      d = addDays(d, 1);
    }
    return days;
  }, [checkin, checkout]);

  // Price breakdown
  const nightlyBreakdown = [];
  let baseTotal = 0;
  if (checkin && checkout) {
    let d = parseFmt(checkin);
    const endD = parseFmt(checkout);
    while (d < endD) {
      const ds = fmt(d);
      const info = calData[ds] || {};
      nightlyBreakdown.push({ date: ds, price: info.price || 0, is_weekend: info.is_weekend });
      baseTotal += info.price || 0;
      d = addDays(d, 1);
    }
  }

  const servicesTotal = Object.entries(selectedServices).reduce((acc, [id, sel]) => {
    if (!sel.selected) return acc;
    const svc = services.find(s => s.id === id);
    if (!svc || svc.supplier_email) return acc; // on-request: exclude from total
    const persons = sel.num_persons || guestForm.num_guests;
    let sub = 0;
    if (svc.service_type === 'meal_plan') {
      if (sel.meal_breakfast) {
        const bd = sel.meal_breakfast_all !== false ? stayDays.length : (sel.meal_breakfast_days || []).length;
        sub += (svc.meal_breakfast_price || 0) * persons * bd;
      }
      if (sel.meal_lunch) {
        const ld = sel.meal_lunch_all !== false ? stayDays.length : (sel.meal_lunch_days || []).length;
        sub += (svc.meal_lunch_price || 0) * persons * ld;
      }
      if (sel.meal_dinner) {
        const dd = sel.meal_dinner_all !== false ? stayDays.length : (sel.meal_dinner_days || []).length;
        sub += (svc.meal_dinner_price || 0) * persons * dd;
      }
    } else {
      const days = sel.applies_to_all_days !== false
        ? nightsCount
        : (sel.selected_days || []).length;
      if (svc.pricing_mode === 'flat') sub = svc.price;
      else if (svc.pricing_mode === 'per_person') sub = svc.price * persons;
      else if (svc.pricing_mode === 'per_day') sub = svc.price * days;
      else if (svc.pricing_mode === 'per_person_per_day') sub = svc.price * persons * days;
    }
    return acc + sub;
  }, 0);

  const extrasTotal = Object.entries(selectedExtras).reduce((acc, [id, qty]) => {
    if (!qty) return acc;
    const ext = extras.find(e => e.id === id);
    if (!ext) return acc;
    const usePrice4 = ext.price_4pax && guestForm.num_guests >= 4;
    return acc + (usePrice4 ? ext.price_4pax : ext.price) * qty;
  }, 0);

  const taxAmount = property ? (property.tax_rate / 100) * baseTotal * guestForm.num_guests : 0;
  const grandTotal = baseTotal + servicesTotal + extrasTotal + taxAmount;

  const handleSubmit = async () => {
    if (!acceptRules) { toast.error('Accettare le regole della casa'); return; }
    if (!guestForm.name || !guestForm.email) { toast.error('Nome e email obbligatori'); return; }
    setSubmitting(true);
    try {
      const svcPayload = Object.entries(selectedServices)
        .filter(([, v]) => v.selected)
        .map(([id, v]) => ({
          service_id: id,
          num_persons: v.num_persons || guestForm.num_guests,
          num_days: v.applies_to_all_days !== false ? nightsCount : (v.selected_days || []).length,
          applies_to_all_days: v.applies_to_all_days !== false,
          selected_days: v.applies_to_all_days !== false ? [] : (v.selected_days || []),
          meal_breakfast: v.meal_breakfast || false,
          meal_lunch: v.meal_lunch || false,
          meal_dinner: v.meal_dinner || false,
          meal_breakfast_days: v.meal_breakfast_all !== false ? [] : (v.meal_breakfast_days || []),
          meal_lunch_days: v.meal_lunch_all !== false ? [] : (v.meal_lunch_days || []),
          meal_dinner_days: v.meal_dinner_all !== false ? [] : (v.meal_dinner_days || []),
          selected_variant_id: v.selected_variant_id || null,
          selected_variant_name: v.selected_variant_name || null,
          custom_field_values: v.custom_field_values || {},
        }));
      const extPayload = Object.entries(selectedExtras)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({ extra_id: id, quantity: qty }));

      const res = await axios.post(`${API}/booking-portal/submit`, {
        property_id: propertyId,
        guest_name: guestForm.name,
        guest_email: guestForm.email,
        guest_phone: guestForm.phone,
        num_guests: guestForm.num_guests,
        special_requests: guestForm.special_requests,
        checkin_date: checkin,
        checkout_date: checkout,
        services: svcPayload,
        extras: extPayload,
      });
      setRequestResult(res.data);
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  const navMonth = (dir) => {
    let m = calMonth + dir;
    let y = calYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCalMonth(m);
    setCalYear(y);
  };

  const extrasByCategory = extras.reduce((acc, e) => {
    const cat = e.category || 'Altro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(e);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex items-center justify-center px-4">
        <div className="text-center max-w-md fade-in">
          <div className="w-16 h-16 bg-[#2D6A4F]/20 border border-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={28} className="text-[#2D6A4F]" />
          </div>
          <h1 className="font-serif text-4xl font-light text-white mb-3">{t('confirmationTitle')}</h1>
          <div className="gold-divider mx-auto" />
          <p className="text-[var(--lux-text-sec)] mt-6 mb-4">{t('confirmationText')}</p>
          {requestResult && (
            <p className="text-[var(--lux-text-muted-2)] text-sm">
              Totale: <span className="text-[#c9a84c]">€{requestResult.total_price?.toFixed(2)}</span>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--lux-bg)]">
      {/* Multi-property selector — shown only when more than 1 property */}
      {allProperties.length > 1 && (
        <div className="bg-[#040404] border-b border-[var(--lux-surface-3)] px-4 py-2 flex items-center gap-2 overflow-x-auto" data-testid="property-selector-bar">
          {allProperties.map(p => (
            <button
              key={p.id}
              data-testid={`property-btn-${p.slug}`}
              onClick={() => { setActiveSlug(p.slug); setStep('calendar'); }}
              className={`shrink-0 px-4 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap ${
                slug === p.slug
                  ? 'border-[#c9a84c] text-[#c9a84c] bg-[#c9a84c]/10'
                  : 'border-[#222] text-[var(--lux-text-muted-3)] hover:border-[var(--lux-border-4)] hover:text-white'
              }`}
            >
              {p.name}{p.city ? ` — ${p.city}` : ''}
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="border-b border-[var(--lux-border-1)] px-6 py-4 flex items-center justify-between sticky top-0 bg-[var(--lux-bg)]/90 backdrop-blur z-40">
        <div className="flex items-center gap-4">
          {/* Branding logo ha priorità su property logo */}
          {wl?.enabled && wl?.logo_url ? (
            <img
              src={wl.logo_url.startsWith('http') ? wl.logo_url : `${process.env.REACT_APP_BACKEND_URL}${wl.logo_url}`}
              alt={wl.brand_name || property?.name}
              className="h-8 object-contain"
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : property?.logo_path ? (
            <img src={`${UPLOADS_BASE}${property.logo_path}`} alt={property.name} className="h-8 object-contain" />
          ) : null}
          <div>
            <h1 className="font-serif text-lg font-light" style={{ color: wl?.enabled && wl?.primary_color ? wl.primary_color : 'white' }}>
              {wl?.enabled && wl?.brand_name ? wl.brand_name : property?.name}
            </h1>
            {property?.city && <p className="text-[var(--lux-text-muted-3)] text-xs">{property.city}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Brochure download */}
          {property?.brochure_path && (
            <a
              href={`${UPLOADS_BASE}${property.brochure_path}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brochure-download-btn"
              className="flex items-center gap-1.5 text-[var(--lux-text-muted-2)] hover:text-[#c9a84c] text-xs uppercase tracking-widest transition-colors border border-[#222] hover:border-[#c9a84c]/50 px-3 py-1.5 rounded"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Brochure
            </a>
          )}
          <button
            onClick={() => setLang(lang === 'it' ? 'en' : 'it')}
            data-testid="lang-toggle"
            className="flex items-center gap-1 text-[var(--lux-text-muted-2)] hover:text-[#c9a84c] text-xs uppercase tracking-widest transition-colors"
          >
            <Globe size={14} />
            {lang === 'it' ? 'EN' : 'IT'}
          </button>
          <button onClick={toggleTheme}
            className="text-[var(--lux-text-muted-2)] hover:text-[var(--lux-text)] transition-colors"
            title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
            data-testid="booking-theme-toggle">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col lg:flex-row gap-8">
        {/* Main form */}
        <div className="flex-1 space-y-8">
          {/* Calendar */}
          <div className="card-dark rounded-lg p-6">
            <p className="label-gold mb-4">Seleziona le date</p>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => navMonth(-1)} className="btn-ghost p-2">
                <ChevronLeft size={18} />
              </button>
              <span className="font-serif text-xl text-white">
                {months[calMonth]} {calYear}
              </span>
              <button onClick={() => navMonth(1)} className="btn-ghost p-2">
                <ChevronRight size={18} />
              </button>
            </div>
            <CalendarMonth
              year={calYear} month={calMonth}
              calData={calData} checkin={checkin} checkout={checkout}
              hoveredDate={hoveredDate}
              onDateHover={setHoveredDate}
              onDateClick={handleDateClick}
              lang={lang}
              minStay={property?.min_stay || 1}
            />
            {property?.min_stay > 1 && (
              <p className="text-[var(--lux-text-muted-3)] text-xs mt-3">
                Soggiorno minimo: {property.min_stay} notti
              </p>
            )}
          </div>

          {/* Services */}
          {checkin && checkout && services.length > 0 && (
            <div ref={servicesRef} className="card-dark rounded-lg p-6 fade-in">
              <p className="label-gold mb-4">Servizi</p>
              <div className="space-y-3">
                {services.map((svc) => {
                  const sel = selectedServices[svc.id] || {};
                  const isSelected = sel.selected || false;
                  const isPerDay = svc.pricing_mode === 'per_day' || svc.pricing_mode === 'per_person_per_day';
                  const isMealPlan = svc.service_type === 'meal_plan';
                  const isRental = svc.service_type === 'rental';
                  const isOnRequest = !!svc.supplier_email;

                  const updateSel = (patch) => setSelectedServices(prev => ({
                    ...prev,
                    [svc.id]: { ...prev[svc.id], ...patch }
                  }));

                  return (
                    <div key={svc.id} className="p-4 bg-[var(--lux-surface-1)] rounded-lg border border-[var(--lux-surface-3)]">
                      {/* Service header */}
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => updateSel({ selected: e.target.checked })}
                          className="mt-1 accent-[#c9a84c]"
                          data-testid={`service-checkbox-${svc.slug}`}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <p className="text-white text-sm font-medium">
                              {lang === 'it' ? svc.name_it : (svc.name_en || svc.name_it)}
                              {isMealPlan && <span className="ml-2 text-[10px] text-[#c9a84c] bg-[#c9a84c]/10 px-1.5 py-0.5 rounded uppercase tracking-wider">Con Chef</span>}
                              {isRental && <span className="ml-2 text-[10px] text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded uppercase tracking-wider">Noleggio</span>}
                            </p>
                            {isOnRequest ? (
                              <span className="text-xs text-amber-400 ml-2 whitespace-nowrap">Su richiesta</span>
                            ) : !isMealPlan ? (
                              <span className="text-[#c9a84c] text-sm ml-2 whitespace-nowrap">€{svc.price}</span>
                            ) : null}
                          </div>
                          {svc.description_it && (
                            <p className="text-[var(--lux-text-muted-2)] text-xs mt-0.5">
                              {lang === 'it' ? svc.description_it : (svc.description_en || svc.description_it)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Expanded options when selected */}
                      {isSelected && (
                        <div className="mt-3 space-y-2">
                          {isMealPlan ? (
                            // Meal plan: show breakfast / lunch / dinner variants
                            <div className="space-y-2">
                              <p className="text-[var(--lux-text-muted-3)] text-xs uppercase tracking-widest">Seleziona i pasti:</p>
                              <MealVariantRow
                                label="Colazione" mealKey="meal_breakfast" daysKey="meal_breakfast_days" allKey="meal_breakfast_all"
                                price={svc.meal_breakfast_price || 0} numPersons={sel.num_persons || guestForm.num_guests}
                                sel={sel} stayDays={stayDays} onUpdate={updateSel}
                              />
                              <MealVariantRow
                                label="Pranzo" mealKey="meal_lunch" daysKey="meal_lunch_days" allKey="meal_lunch_all"
                                price={svc.meal_lunch_price || 0} numPersons={sel.num_persons || guestForm.num_guests}
                                sel={sel} stayDays={stayDays} onUpdate={updateSel}
                              />
                              <MealVariantRow
                                label="Cena" mealKey="meal_dinner" daysKey="meal_dinner_days" allKey="meal_dinner_all"
                                price={svc.meal_dinner_price || 0} numPersons={sel.num_persons || guestForm.num_guests}
                                sel={sel} stayDays={stayDays} onUpdate={updateSel}
                              />
                              {isOnRequest && (
                                <p className="text-amber-400/70 text-xs mt-1">Il fornitore riceverà la vostra richiesta e vi invierà il preventivo.</p>
                              )}
                            </div>
                          ) : isRental ? (
                            // Rental/NCC: variant selector + custom fields
                            <RentalSelector
                              svc={svc} sel={sel} stayDays={stayDays}
                              numGuests={guestForm.num_guests} onUpdate={updateSel} lang={lang}
                            />
                          ) : isPerDay ? (
                            // Per-day service: show day selector
                            <PerDaySelector
                              stayDays={stayDays}
                              sel={sel}
                              nightsCount={nightsCount}
                              onUpdate={updateSel}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Extras */}
          {checkin && checkout && extras.length > 0 && (
            <div className="card-dark rounded-lg p-6 fade-in">
              <p className="label-gold mb-4">Extra & Bevande</p>
              {Object.entries(extrasByCategory).map(([cat, items]) => (
                <div key={cat} className="mb-6">
                  <p className="text-[var(--lux-text-muted-2)] text-xs uppercase tracking-widest mb-3">{cat}</p>
                  <div className="space-y-2">
                    {items.map((ext) => {
                      const qty = selectedExtras[ext.id] || 0;
                      return (
                        <div key={ext.id} className="flex items-center justify-between p-3 bg-[var(--lux-surface-1)] rounded-lg">
                          <div>
                            <p className="text-white text-sm">{ext.name}</p>
                            {ext.variant && <p className="text-[var(--lux-text-muted-2)] text-xs">{ext.variant}{ext.size ? ` · ${ext.size}` : ''}</p>}
                            <p className="text-[#c9a84c] text-xs mt-0.5">€{ext.price}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedExtras(prev => ({ ...prev, [ext.id]: Math.max(0, (prev[ext.id] || 0) - 1) }))}
                              className="w-7 h-7 border border-[var(--lux-border-3)] flex items-center justify-center text-[#c9a84c] hover:bg-[var(--lux-border-1)] rounded"
                              data-testid={`extra-minus-${ext.id}`}
                            >
                              <Minus size={12} />
                            </button>
                            <span className="w-6 text-center text-white text-sm" data-testid={`extra-qty-${ext.id}`}>{qty}</span>
                            <button
                              onClick={() => setSelectedExtras(prev => ({ ...prev, [ext.id]: (prev[ext.id] || 0) + 1 }))}
                              className="w-7 h-7 border border-[var(--lux-border-3)] flex items-center justify-center text-[#c9a84c] hover:bg-[var(--lux-border-1)] rounded"
                              data-testid={`extra-plus-${ext.id}`}
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Guest Info */}
          {checkin && checkout && (
            <div ref={services.length === 0 ? servicesRef : null} className="card-dark rounded-lg p-6 fade-in">
              <p className="label-gold mb-4">Informazioni Ospite</p>
              <div className="space-y-4">
                <div>
                  <label className="label-muted block mb-1">Nome e Cognome *</label>
                  <input
                    className="input-dark"
                    placeholder="Mario Rossi"
                    value={guestForm.name}
                    onChange={e => setGuestForm(p => ({ ...p, name: e.target.value }))}
                    data-testid="guest-name-input"
                  />
                </div>
                <div>
                  <label className="label-muted block mb-1">Email *</label>
                  <input
                    className="input-dark"
                    type="email"
                    placeholder="mario@email.com"
                    value={guestForm.email}
                    onChange={e => setGuestForm(p => ({ ...p, email: e.target.value }))}
                    data-testid="guest-email-input"
                  />
                </div>
                <div>
                  <label className="label-muted block mb-1">Telefono</label>
                  <input
                    className="input-dark"
                    placeholder="+39 000 0000000"
                    value={guestForm.phone}
                    onChange={e => setGuestForm(p => ({ ...p, phone: e.target.value }))}
                    data-testid="guest-phone-input"
                  />
                </div>
                <div>
                  <label className="label-muted block mb-1">Numero ospiti</label>
                  <input
                    className="input-dark"
                    type="number"
                    min={1}
                    max={property?.max_guests || 20}
                    value={guestForm.num_guests}
                    onChange={e => setGuestForm(p => ({ ...p, num_guests: parseInt(e.target.value) || 1 }))}
                    data-testid="guest-count-input"
                  />
                </div>
                <div>
                  <label className="label-muted block mb-1">Richieste speciali</label>
                  <textarea
                    className="input-dark h-20 resize-none"
                    placeholder="Es. arrivo tardivo, allergie alimentari..."
                    value={guestForm.special_requests}
                    onChange={e => setGuestForm(p => ({ ...p, special_requests: e.target.value }))}
                    data-testid="guest-requests-input"
                  />
                </div>

                {/* House rules */}
                {(property?.house_rules || property?.house_rules_en) && (
                  <div className="border border-[var(--lux-border-1)] rounded-lg p-4">
                    <p className="label-gold text-xs mb-3">Regole della Casa</p>
                    <p className="text-[var(--lux-text-sec)] text-xs whitespace-pre-wrap">
                      {lang === 'it' ? property.house_rules : (property.house_rules_en || property.house_rules)}
                    </p>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptRules}
                    onChange={e => setAcceptRules(e.target.checked)}
                    className="mt-0.5 accent-[#c9a84c]"
                    data-testid="accept-rules-checkbox"
                  />
                  <span className="text-[var(--lux-text-sec)] text-xs">
                    Ho letto e accetto le regole della casa
                  </span>
                </label>

                <button
                  onClick={handleSubmit}
                  disabled={submitting || !acceptRules}
                  className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-40"
                  data-testid="submit-booking-btn"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  {submitting ? 'Invio...' : 'Invia Richiesta di Prenotazione'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        {(checkin || nightsCount > 0) && (
          <div className="lg:w-80 lg:sticky lg:top-24 lg:self-start fade-in">
            <div className="card-dark rounded-lg p-6">
              <p className="label-gold mb-4">Riepilogo</p>
              {checkin && (
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--lux-text-muted-2)]">Check-in</span>
                    <span className="text-white">{new Date(checkin + 'T12:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {checkout && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--lux-text-muted-2)]">Check-out</span>
                      <span className="text-white">{new Date(checkout + 'T12:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                  {nightsCount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--lux-text-muted-2)]">Notti</span>
                      <span className="text-white">{nightsCount}</span>
                    </div>
                  )}
                </div>
              )}
              {nightsCount > 0 && (
                <>
                  <div className="border-t border-[var(--lux-border-1)] pt-4 space-y-2">
                    {nightlyBreakdown.map(n => (
                      <div key={n.date} className="flex justify-between text-xs">
                        <span className="text-[var(--lux-text-muted-3)]">{n.date}{n.is_weekend ? ' (wknd)' : ''}</span>
                        <span className="text-[var(--lux-text-sec)]">€{n.price?.toFixed(2)}</span>
                      </div>
                    ))}
                    {servicesTotal > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--lux-text-muted-3)]">Servizi</span>
                        <span className="text-[var(--lux-text-sec)]">€{servicesTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {/* On-request services listed separately */}
                    {Object.entries(selectedServices).some(([id, sel]) => sel.selected && services.find(s => s.id === id)?.supplier_email) && (
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--lux-text-muted-3)]">Servizi su richiesta</span>
                        <span className="text-amber-400">Da concordare</span>
                      </div>
                    )}
                    {extrasTotal > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--lux-text-muted-3)]">Extra</span>
                        <span className="text-[var(--lux-text-sec)]">€{extrasTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--lux-text-muted-3)]">Tassa soggiorno</span>
                        <span className="text-[var(--lux-text-sec)]">€{taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-[#222] pt-4 mt-4 flex justify-between items-center">
                    <span className="label-muted">Totale stimato</span>
                    <span className="font-serif text-2xl text-[#c9a84c]">
                      €{grandTotal.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-[var(--lux-border-4)] text-xs mt-2">Il totale è indicativo e potrebbe variare in base ai servizi richiesti.</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="border-t border-[var(--lux-border-1)] py-4 text-center">
        <p className="text-[var(--lux-text-muted-3)] text-[10px]">
          Sviluppato da{' '}
          <a href="https://francescorusso.work" target="_blank" rel="noreferrer"
            className="hover:text-[var(--lux-text-muted)] transition-colors underline">
            Francesco Russo
          </a>
        </p>
      </div>

      {/* AI Booking Assistant Widget */}
      {tenantSlug && (
        <AIBookingAssistant
          tenantSlug={tenantSlug}
          propertyId={propertyId || activeSlug}
          checkin={checkin}
          checkout={checkout}
          guests={guestForm.num_guests || 2}
          lang={lang}
          onAddService={(suggestion) => {
            toast.success(`"${suggestion.title}" aggiunto al tuo soggiorno!`);
          }}
        />
      )}
    </div>
  );
}
